import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getSectionAnswers, QITAnswer } from 'src/api/dataVault/sectionAnswers';

type Status = 'idle' | 'loading' | 'succeeded' | 'failed';

interface Entry {
  status: Status;
  rows: QITAnswer[];
  error: string | null;
  lastFetchedAt: number | null;
}

interface State {
  bySectionKey: Record<string, Entry>;
}

const STALE_MS = 30_000;

const initialState: State = { bySectionKey: {} };

function sectionKey(targetUserId: number, sectionId: string): string {
  return `${targetUserId}:${sectionId}`;
}

export const fetchSectionAnswers = createAsyncThunk(
  'dataVaultSectionDetail/fetch',
  async (
    {
      targetUserId,
      sectionId,
      actingOnMemberId,
      forceRefresh = false,
    }: {
      targetUserId: number;
      sectionId: string;
      actingOnMemberId?: number | null;
      forceRefresh?: boolean;
    },
    { getState, rejectWithValue }
  ) => {
    const state = (getState() as { dataVaultSectionDetail: State })
      .dataVaultSectionDetail;
    const key = sectionKey(targetUserId, sectionId);
    const entry = state.bySectionKey[key];
    if (
      !forceRefresh &&
      entry?.status === 'succeeded' &&
      entry.lastFetchedAt &&
      Date.now() - entry.lastFetchedAt < STALE_MS
    ) {
      return null; // cached
    }
    try {
      return await getSectionAnswers(sectionId, { actingOnMemberId });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } } };
      return rejectWithValue(
        err?.response?.data?.detail ?? 'Failed to load section answers'
      );
    }
  }
);

const slice = createSlice({
  name: 'dataVaultSectionDetail',
  initialState,
  reducers: {
    invalidateSection(
      state,
      action: PayloadAction<{ targetUserId: number; sectionId: string }>
    ) {
      const key = sectionKey(
        action.payload.targetUserId,
        action.payload.sectionId
      );
      delete state.bySectionKey[key];
    },
    clearEntity(
      state,
      action: PayloadAction<{ targetUserId: number; sectionId: string }>
    ) {
      const key = sectionKey(
        action.payload.targetUserId,
        action.payload.sectionId
      );
      delete state.bySectionKey[key];
    },
    clearAllForUser(state, action: PayloadAction<number>) {
      const prefix = `${action.payload}:`;
      Object.keys(state.bySectionKey).forEach((k) => {
        if (k.startsWith(prefix)) {
          delete state.bySectionKey[k];
        }
      });
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSectionAnswers.pending, (state, action) => {
        const key = sectionKey(
          action.meta.arg.targetUserId,
          action.meta.arg.sectionId
        );
        state.bySectionKey[key] = {
          status: 'loading',
          rows: [],
          error: null,
          lastFetchedAt: null,
        };
      })
      .addCase(fetchSectionAnswers.fulfilled, (state, action) => {
        const key = sectionKey(
          action.meta.arg.targetUserId,
          action.meta.arg.sectionId
        );
        if (action.payload === null) {
          return;
        } // cached
        state.bySectionKey[key] = {
          status: 'succeeded',
          rows: action.payload,
          error: null,
          lastFetchedAt: Date.now(),
        };
      })
      .addCase(fetchSectionAnswers.rejected, (state, action) => {
        const key = sectionKey(
          action.meta.arg.targetUserId,
          action.meta.arg.sectionId
        );
        state.bySectionKey[key] = {
          status: 'failed',
          rows: [],
          error: action.payload as string,
          lastFetchedAt: null,
        };
      });
  },
});

export const { invalidateSection, clearEntity, clearAllForUser } =
  slice.actions;

export const selectSectionAnswers =
  (targetUserId: number, sectionId: string) =>
  (state: { dataVaultSectionDetail: State }): Entry | undefined =>
    state.dataVaultSectionDetail.bySectionKey[
      sectionKey(targetUserId, sectionId)
    ];

export default slice.reducer;
